import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SuperAdminLayout } from '../../components/SuperAdminLayout';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Users, Plus, Pencil, Search, Loader2, X, ArrowLeft,
  ChevronLeft, ChevronRight, Shield
} from 'lucide-react';

const ROLE_LABELS = { 1: 'Admin', 2: 'Manager', 3: 'Agent', 4: 'Customer' };
const ROLE_COLORS = {
  1: 'bg-indigo-100 text-indigo-700',
  2: 'bg-purple-100 text-purple-700',
  3: 'bg-blue-100 text-blue-700',
  4: 'bg-muted text-gray-600',
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-red-100 text-red-700',
};

const EMPTY_FORM = { name: '', email: '', password: '', status: 'active' };
const EMPTY_EDIT = { name: '', email: '', password: '', status: 'active', user_type: 1 };

export default function CompanyUsersPage() {
  const { company_id } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Add admin modal
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);

  // Edit modal
  const [editModal, setEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editSaving, setEditSaving] = useState(false);

  const fetchCompany = useCallback(async () => {
    try {
      const res = await api.get(`/superadmin/companies/${company_id}`);
      setCompany(res.data?.company || res.data);
    } catch {
      toast.error('Failed to load company');
    }
  }, [company_id]);

  const fetchUsers = useCallback(async (p = page, s = search) => {
    setLoading(true);
    try {
      const res = await api.get(`/superadmin/companies/${company_id}/users`, {
        params: { page: p, limit: 30, search: s },
      });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [company_id, page, search]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);
  useEffect(() => { fetchUsers(page, search); }, [page]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers(1, search);
  };

  // Add admin
  const openAdd = () => { setAddForm(EMPTY_FORM); setAddModal(true); };
  const handleAdd = async () => {
    if (!addForm.name.trim() || !addForm.email.trim() || !addForm.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    setAddSaving(true);
    try {
      await api.post(`/superadmin/companies/${company_id}/users`, addForm);
      toast.success('Admin account created');
      setAddModal(false);
      fetchUsers(1, search);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setAddSaving(false);
    }
  };

  // Edit user
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, email: u.email, password: '', status: u.status, user_type: u.user_type });
    setEditModal(true);
  };
  const handleEdit = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/superadmin/companies/${company_id}/users/${editUser.user_id}`, editForm);
      toast.success('User updated');
      setEditModal(false);
      fetchUsers(page, search);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <SuperAdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/superadmin/companies')}
              className="p-2 text-muted-foreground hover:text-gray-800 hover:bg-muted rounded-lg transition-colors"
              data-testid="back-to-companies"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                <h1 className="text-2xl font-bold text-foreground">
                  {company?.name || 'Company'} — Users
                </h1>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{total} total users</p>
            </div>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            data-testid="add-admin-btn"
          >
            <Plus className="w-4 h-4" />
            Add Admin
          </button>
        </div>

        {/* Search */}
        <div className="bg-card rounded-xl border border-border p-4 mb-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-9 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="user-search-input"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(''); setPage(1); fetchUsers(1, ''); }}
                className="px-3 py-2 text-muted-foreground border border-border rounded-lg hover:bg-muted text-sm"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
        </div>

        {/* Table */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No users found</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">Name</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">Email</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">Role</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-6 py-3 text-right font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map((u, i) => (
                  <tr key={u.user_id || i} className="hover:bg-muted transition-colors" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-sm flex-shrink-0">
                          {(u.name || 'U')[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-foreground">{u.name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[u.user_type] || 'bg-muted text-gray-600'}`}>
                        {u.user_type <= 3 && <Shield className="w-3 h-3" />}
                        {ROLE_LABELS[u.user_type] || `Type ${u.user_type}`}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[u.status] || 'bg-muted text-muted-foreground'}`}>
                        {u.status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => openEdit(u)}
                        className="p-2 text-muted-foreground hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        data-testid={`edit-user-${u.user_id}`}
                        title="Edit User"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-border flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages} · {total} users</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Admin Modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !addSaving && setAddModal(false)} role="presentation">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-md app-modal-shell" onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-bold text-foreground">Add Admin Account</h2>
              <button onClick={() => setAddModal(false)} className="p-2 hover:bg-muted rounded-lg">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="app-modal-body p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Name *</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Full name"
                  data-testid="add-admin-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="admin@example.com"
                  data-testid="add-admin-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Password *</label>
                <input
                  type="password"
                  value={addForm.password}
                  onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Min 6 characters"
                  data-testid="add-admin-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                <select
                  value={addForm.status}
                  onChange={e => setAddForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="add-admin-status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="app-modal-footer flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => setAddModal(false)} className="px-4 py-2 text-sm text-gray-600 border border-border rounded-lg hover:bg-muted">
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={addSaving}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
                data-testid="add-admin-submit"
              >
                {addSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Admin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && editUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditModal(false)} role="presentation">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-md app-modal-shell" onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-bold text-foreground">Edit User</h2>
              <button onClick={() => setEditModal(false)} className="p-2 hover:bg-muted rounded-lg">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="app-modal-body p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="edit-user-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">New Password <span className="text-muted-foreground font-normal">(leave blank to keep current)</span></label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Leave blank to keep"
                  data-testid="edit-user-password"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Role</label>
                  <select
                    value={editForm.user_type}
                    onChange={e => setEditForm(f => ({ ...f, user_type: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="edit-user-role"
                  >
                    <option value={1}>Admin</option>
                    <option value={2}>Manager</option>
                    <option value={3}>Agent</option>
                    <option value={4}>Customer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="edit-user-status"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="app-modal-footer flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => setEditModal(false)} className="px-4 py-2 text-sm text-gray-600 border border-border rounded-lg hover:bg-muted">
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={editSaving}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
                data-testid="edit-user-submit"
              >
                {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
}
